import { useEffect, useRef } from 'react';
import type { PrintOptionsState, SearchState } from '../types';
import {
  normalizeSearchTextFields,
  toNumber,
  toSearchTermType,
  toSearchTextType,
  toSortValue,
} from '../utils';
import { getInitialPrintOptions } from '../constants';

interface Args {
  param: Record<string, unknown> | undefined;
  urlSearchParams: URLSearchParams;
  setSearch: (next: SearchState) => void;
  setAppliedSearch: (next: SearchState | null) => void;
  setSearchApplied: (applied: boolean) => void;
  setPrintOptions: (next: PrintOptionsState) => void;
}

export function useSearchInit({
  param,
  urlSearchParams,
  setSearch,
  setAppliedSearch,
  setSearchApplied,
  setPrintOptions,
}: Args) {
  const initializedRef = useRef(false);
  const printInitializedRef = useRef(false);

  useEffect(() => {
    if (!param || initializedRef.current) return;
    initializedRef.current = true;

    const p = param as Record<string, unknown>;
    const qsSearchTermType = urlSearchParams.get('search_term_type');
    const qsSort = urlSearchParams.get('sort');
    const qsSearchText = urlSearchParams.get('search_text');
    const defaultSearchTreatment = (p.search_treatment as string) ?? '';
    const defaultSearchTreatmentYearmonth =
      (p.search_treatment_yearmonth as string) ??
      (defaultSearchTreatment.length >= 8 ? defaultSearchTreatment.slice(0, 8) : '');
    const nextSearch: SearchState = normalizeSearchTextFields({
      search_term_type: toSearchTermType(qsSearchTermType ?? p.search_term_type),
      search_treatment: urlSearchParams.get('search_treatment') ?? defaultSearchTreatment,
      search_treatment_yearmonth:
        urlSearchParams.get('search_treatment_yearmonth') ?? defaultSearchTreatmentYearmonth,
      search_treatement_from:
        urlSearchParams.get('search_treatement_from') ?? (p.search_treatement_from as string) ?? '',
      search_treatement_to:
        urlSearchParams.get('search_treatement_to') ?? (p.search_treatement_to as string) ?? '',
      patient_cost_rate:
        urlSearchParams.get('patient_cost_rate') ??
        (p.patient_cost_rate != null ? String(p.patient_cost_rate) : ''),
      bui: urlSearchParams.get('bui') ?? (p.bui != null ? String(p.bui) : ''),
      shoken1:
        (urlSearchParams.get('shoken1') != null
          ? toNumber(urlSearchParams.get('shoken1'), 0)
          : toNumber(p.shoken1, 0)) === 1,
      shoken2:
        (urlSearchParams.get('shoken2') != null
          ? toNumber(urlSearchParams.get('shoken2'), 0)
          : toNumber(p.shoken2, 0)) === 1,
      search_text: toSearchTextType(qsSearchText ?? p.search_text),
      search_patient_id:
        urlSearchParams.get('search_patient_id') ?? (p.search_patient_id as string) ?? '',
      search_patient_name:
        urlSearchParams.get('search_patient_name') ?? (p.search_patient_name as string) ?? '',
      ini: urlSearchParams.get('ini') ?? (p.ini as string) ?? '',
      sort: toSortValue(qsSort ?? p.sort),
    });
    const isRun = urlSearchParams.get('run') === '1';

    setSearch(nextSearch);
    setAppliedSearch(isRun ? nextSearch : null);
    setSearchApplied(isRun);
  }, [param, urlSearchParams, setSearch, setAppliedSearch, setSearchApplied]);

  useEffect(() => {
    if (!param || printInitializedRef.current) return;
    printInitializedRef.current = true;

    const p = param as Record<string, unknown>;
    const qsSearchTermType = urlSearchParams.get('search_term_type');
    const initialTermType = toSearchTermType(qsSearchTermType ?? p.search_term_type);

    setPrintOptions({
      ...getInitialPrintOptions(),
      print_monthly: initialTermType === 2,
    });
  }, [param, urlSearchParams, setPrintOptions]);
}
